import _ from 'lodash';
import { Navigate, useLocation } from 'react-router-dom';
import { ErrorBoundary } from '../errors/error-boundary.component';
import userService from 'shared/services/user.service';
import Logger from 'shared/utils/Logger';
import { isAuthenticated } from 'shared/store/selectors/auth.selector';
import {
  markAuthenticatedThisLoad,
  setPostLoginRedirect,
  wasAuthenticatedThisLoad,
} from 'src/utils/post-login-redirect';
import { usePostLoginRedirect } from 'src/hooks/use-post-login-redirect';

const logger = Logger.create({ loggerName: 'Route' });

export const Route = ({
  element,
  isAllowedRoute,
  redirectRoute,
  isHavePermissions = null,
  rootPath,
  auth,
  // PrivateRoute: stash the protected route the user was denied, to return to after login.
  shouldCaptureIntendedRoute = false,
  // AuthRoute: once authenticated, send the user to the stashed route instead of the default.
  shouldRestoreIntendedRoute = false,
}) => {
  const location = useLocation();

  if (isAuthenticated({ auth })) markAuthenticatedThisLoad();

  const restoredTarget = usePostLoginRedirect(
    isAllowedRoute && shouldRestoreIntendedRoute,
    redirectRoute,
  );

  if (isAllowedRoute) {
    /* How to read the logs:
     - isLogout: true + signOutCode: USER_INITIATED → user clicked logout
     - isLogout: true + signOutCode: <other value> → forced logout (token refresh failure, etc.)
     - isLogout: false + signOutCode: null → store bug / unexpected state loss (the case we're investigating) */
    logger.warn('Route redirect triggered', {
      redirectRoute,
      from: location.pathname,
      userId:
        auth?.id || auth?.user?.id || auth?.refreshTokenDetails?.id || userService.userId || null,
      hasRefreshTokenDetails: !_.isEmpty(auth?.refreshTokenDetails),
      hasTokenDetails: !_.isEmpty(auth?.tokenDetails),
      isLogout:
        Boolean(auth?.user?.email) &&
        _.isEmpty(auth?.tokenDetails) &&
        _.isEmpty(auth?.refreshTokenDetails),
      signOutCode: auth?.signOutCode || null,
      refreshTokenDetails: auth?.refreshTokenDetails,
      tokenDetails: auth?.tokenDetails,
    });

    // Skip on a live logout so re-login lands on home, not the last page; only
    // stash for a genuine deep link opened while never authenticated this load.
    if (shouldCaptureIntendedRoute && !wasAuthenticatedThisLoad()) {
      setPostLoginRedirect(location.pathname + location.search);
    }

    return <Navigate to={restoredTarget} state={{ from: location }} replace />;
  }

  if (!_.isNull(isHavePermissions) && !isHavePermissions) {
    return <Navigate to={rootPath} state={{ from: location }} replace />;
  }

  return <ErrorBoundary>{element}</ErrorBoundary>;
};
